import React, { useMemo } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { MapPin, ArrowRight } from 'lucide-react';
import Header from '../components/landing/Header';
import FormsSection from '../components/landing/FormsSection';
import HowItWorksSection from '../components/landing/HowItWorksSection';
import TrustSection from '../components/landing/TrustSection';
import Footer from '../components/landing/Footer';
import MarketHero from '../components/landing/MarketHero';
import MarketSellerBuyerSplit from '../components/landing/MarketSellerBuyerSplit';
import SEOHead from '../components/SEOHead';
import { Toaster } from '../components/ui/sonner';
import { getStateBySlug } from '../data/states';
import { cities } from '../data/cities';

const buildStateLocalBusinessJsonLd = (stateInfo, canonical) => ({
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: `DreamHousesUSA — ${stateInfo.name}`,
  description: stateInfo.metaDescription,
  url: canonical,
  email: 'info@dreamhousesusa.com',
  areaServed: {
    '@type': 'State',
    name: stateInfo.name,
    containedInPlace: { '@type': 'Country', name: 'United States' },
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: stateInfo.geo.lat,
    longitude: stateInfo.geo.lon,
  },
  knowsAbout: [
    `${stateInfo.name} real estate wholesaling`,
    `Cash home buyers ${stateInfo.name}`,
    `Off-market deals ${stateInfo.name}`,
    `${stateInfo.name} investment properties`,
  ],
});

const buildStateBreadcrumb = (stateInfo, canonical) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.dreamhousesusa.com/' },
    { '@type': 'ListItem', position: 2, name: 'Markets', item: 'https://www.dreamhousesusa.com/#markets' },
    { '@type': 'ListItem', position: 3, name: stateInfo.name, item: canonical },
  ],
});

const buildStateFaqJsonLd = (stateInfo, stateCities) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: `Do you buy houses anywhere in ${stateInfo.name}?`,
      acceptedAnswer: {
        '@type': 'Answer',
        text: `Yes — we buy houses across ${stateInfo.name} including ${stateCities.map((c) => c.name).join(', ')} and surrounding metros. Any condition, any price range, all cash.`,
      },
    },
    {
      '@type': 'Question',
      name: `How fast can you close on a ${stateInfo.name} property?`,
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Most closings happen in 7–21 days. We handle title work, back taxes, code violations, and probate as needed.',
      },
    },
    {
      '@type': 'Question',
      name: `Are you a real estate brokerage in ${stateInfo.name}?`,
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'No — DreamHousesUSA is a real estate investment and wholesaling company. We buy properties directly for cash or assign contracts to qualified investors. We do not list properties on the MLS.',
      },
    },
  ],
});

const StateStatsBar = ({ stats }) => (
  <section className="py-12 bg-white border-y border-slate-100">
    <div className="container mx-auto px-6 max-w-6xl">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        {stats.map((s) => (
          <div key={s.label} className="text-center">
            <div className="text-3xl md:text-4xl font-bold text-emerald-600 mb-1">{s.value}</div>
            <div className="text-sm text-slate-600">{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const StateCityCard = ({ city }) => (
  <Link
    key={city.slug}
    to={`/we-buy-houses/${city.slug}`}
    data-testid={`state-city-${city.slug}`}
    className="group p-5 rounded-xl border border-slate-200 hover:border-emerald-400 hover:shadow-md transition-all bg-white"
  >
    <div className="flex items-start justify-between">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <MapPin className="w-4 h-4 text-emerald-600" />
          <span className="text-sm font-medium text-slate-500">{city.state}</span>
        </div>
        <h3 className="text-lg font-bold text-slate-900 group-hover:text-emerald-600 transition-colors">
          We Buy Houses in {city.name}
        </h3>
        <p className="text-sm text-slate-600 mt-1 line-clamp-2">
          {city.zips.length} ZIP codes • {city.neighborhoods.length} neighborhoods
        </p>
      </div>
      <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-emerald-600 group-hover:translate-x-1 transition-all flex-shrink-0 ml-2" />
    </div>
  </Link>
);

const StateCitiesSection = ({ stateInfo, stateCities }) => (
  <section className="py-16 bg-white">
    <div className="container mx-auto px-6 max-w-6xl">
      <h2 className="text-3xl font-bold text-slate-900 mb-2">
        {stateInfo.name} Cities We Buy Houses In
      </h2>
      <p className="text-slate-600 mb-8">
        Click your city for local neighborhoods, ZIP codes, and market intel.
      </p>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {stateCities.map((c) => <StateCityCard key={c.slug} city={c} />)}
      </div>
      <div className="mt-10">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">Counties Served</h3>
        <div className="flex flex-wrap gap-2">
          {stateInfo.counties.map((county) => (
            <span key={county} className="px-3 py-1.5 bg-slate-100 rounded-full text-sm text-slate-700">
              {county} County
            </span>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const StatePage = () => {
  const { slug } = useParams();
  const stateInfo = getStateBySlug(slug);

  const stateCities = useMemo(
    () => (stateInfo ? cities.filter((c) => c.stateAbbr === stateInfo.stateAbbr) : []),
    [stateInfo]
  );

  if (!stateInfo) {
    return <Navigate to="/" replace />;
  }

  const canonical = `https://www.dreamhousesusa.com/we-buy-houses/${stateInfo.slug}`;
  const jsonLd = buildStateLocalBusinessJsonLd(stateInfo, canonical);
  const breadcrumb = buildStateBreadcrumb(stateInfo, canonical);
  const faq = buildStateFaqJsonLd(stateInfo, stateCities);

  return (
    <div className="min-h-screen" data-testid={`state-page-${stateInfo.slug}`}>
      <Toaster position="top-right" richColors />
      <SEOHead
        title={stateInfo.metaTitle}
        description={stateInfo.metaDescription}
        keywords={stateInfo.keywords}
        canonical={canonical}
        jsonLd={jsonLd}
        jsonLdId="state-localbusiness-jsonld"
      />
      <SEOHead jsonLd={breadcrumb} jsonLdId="state-breadcrumb-jsonld" />
      <SEOHead jsonLd={faq} jsonLdId="state-faq-jsonld" />
      <Header />
      <main className="pt-16">
        <MarketHero
          badge={`Statewide — ${stateInfo.name}`}
          headline={stateInfo.headline}
          intro={stateInfo.intro}
          ctaLabel={stateInfo.name}
        />
        <StateStatsBar stats={stateInfo.stats} />
        <MarketSellerBuyerSplit
          marketName={stateInfo.name}
          sellerCopy={stateInfo.sellerCopy}
          buyerCopy={stateInfo.buyerCopy}
          bg="slate-50"
        />
        <StateCitiesSection stateInfo={stateInfo} stateCities={stateCities} />
        <HowItWorksSection />
        <FormsSection
          source={`state:${stateInfo.slug}`}
          sourceCityState={stateInfo.name}
          sourceMarket={stateInfo.name}
        />
        <TrustSection />
      </main>
      <Footer />
    </div>
  );
};

export default StatePage;
